<template>
  <div>
    <article class="project-analysis-item__wrapper">
      <AnalysisSettings
        ref="AnalysisSettings"
        :statusDisableActionBtns="statusDisableActionBtns"
        :index="index"
        :thisAnalysisName="outlierAnalysisTitle"
        :isOutliersAnalysis="true"
        :propReadOnly="propReadOnly"
        :savedNodes="
          this.outliersAnalysisObject.saved_node_ids
            ? this.outliersAnalysisObject.saved_node_ids
            : []
        "
        :contentChanged="this.contentChanged"
        :hideSave="true"
        :lastModified="
          formatDateTime(outliersAnalysisObject.last_modified_on, $store.getters.getLanguage)
        "
        @toggled="isExpanded = !isExpanded"
        @filterTop="updateFilterTop"
        @changeAnalysisTitle="renameAnalysis"
        :totalOutliers="outliersAnalysisObject.outliers.length"
      ></AnalysisSettings>
      <div v-if="isExpanded">
        <LoadingSpinner
          :isLoading="isLoadingData"
          :componentStyle="true"
          style="position: relative"
          :style="{ 'min-height': minHeight }"
        />
        <div v-if="!isLoadingData">
          <SavedOutliers
            :propReadOnly="propReadOnly"
            :outliersAnalysisObject="outliersAnalysisObject"
            @delete-outlier="deleteOutlier"
          ></SavedOutliers>
          <TopOutliers
            :propReadOnly="propReadOnly"
            :data="outliersAnalysisObject"
            @saveThisOutlier="saveThisOutlierToProject"
          ></TopOutliers>
        </div>
      </div>
    </article>
  </div>
</template>

<script>
// Components
import TopOutliers from './Components/TopOutliers.vue';
import SavedOutliers from './Components/SavedOutliers.vue';
import LoadingSpinner from '@/components/UI/Spinner.vue';

import AnalysisSettings from '@/components/Project/ProjectAnalysis/Components/AnalysisSettings.vue';

// Helpers
import moment from 'moment';

// Mixins
import ProjectMixin from '@/components/Project/Mixins/projectMixin.js';
import ProjectReportMixin from '@/components/Project/Mixins/projectReportMixin.js';
import FiltersMixin from '@/utils/mixins/userMixin.js';
import AnalysisMixin from '@/components/Project/ProjectAnalysis/Components/analysisMixin.js';
import DatasetDetailsMixin from '@/utils/mixins/datasetDetailsMixin.js';

export default {
  name: 'ProjectAnalysisOutliersItem',
  components: {
    TopOutliers,
    SavedOutliers,
    AnalysisSettings,
    LoadingSpinner,
  },
  mixins: [ProjectMixin, ProjectReportMixin, FiltersMixin, AnalysisMixin, DatasetDetailsMixin],
  props: {
    index: {
      type: Number,
      required: true,
    },
    propReadOnly: {
      default: () => false,
      type: Boolean,
    },
    expandThisAnalysis: {
      default: () => false,
      type: Boolean,
    },
    renameThisAnalysis: {
      default: () => false,
      type: Boolean,
    },
    saveThisAnalysis: {
      default: () => false,
      type: Boolean,
    },
    slideName: {
      default: () => '',
      type: String,
    },
    slideForSaving: {
      default: () => {},
      type: Object,
    },
    setToUpdateTimeFilter: {
      default: () => false,
      type: Boolean,
    },
    selectedTimeFilter: {
      default: () => 'All data',
      type: String,
    },
    selectedTimeSeries: {
      default: () => 'Days',
      type: String,
    },
    setToUpdateTimeSeries: {
      default: () => false,
      type: Boolean,
    },
  },
  data() {
    return {
      savedSlide: '',
      outliersAnalysisObject: {},
      isLoadingData: false,
      uuid: this.$pigeonline.createUUID(),
      isExpanded: false,
      contentChanged: false,
    };
  },
  async created() {
    if (this.project) {
      if (this.project.outliersAnalysis.length) {
        this.outliersAnalysisObject = this.project.outliersAnalysis[this.index];
        await this.checkForOldSavedNodes(); // Prior to 1.10.0, old nodes were saved directly, wasting space. Revert back to IDs
        if (
          !this.outliersAnalysisObject.analysis_title ||
          !this.outliersAnalysisObject.analysis_title.length
        ) {
          let outlierTitle = '';
          if (this.outliersAnalysisObject.selectedQuestions.length) {
            outlierTitle = 'Across ' + this.outliersAnalysisObject.selectedQuestions.length;
            if (this.outliersAnalysisObject.selectedQuestions.length == 1) {
              outlierTitle = outlierTitle + ' scale question';
            } else {
              outlierTitle = outlierTitle + ' scale questions';
            }
          }
          if (outlierTitle.length) {
            this.renameAnalysis(outlierTitle);
          }
        }
      }
    }
  },
  computed: {
    statusDisableActionBtns() {
      return this.isLoadingData || !this.outliersAnalysisObject.outliers.length;
    },
    minHeight() {
      if (this.isLoadingData) {
        return '5em';
      } else {
        return '0';
      }
    },
    outlierAnalysisTitle() {
      let outlierTitle = '';
      if (this.outliersAnalysisObject) {
        if (
          this.outliersAnalysisObject.analysis_title &&
          this.outliersAnalysisObject.analysis_title.length
        ) {
          outlierTitle = this.outliersAnalysisObject.analysis_title;
        }
      }
      return outlierTitle;
    },
    OUTLIERS_SERVICE() {
      return this.$services.OUTLIERS_SERVICE;
    },
  },
  methods: {
    // In legacy objects, saved_node_ids were actually full nodes, wasteful. Revert back to ids. These are just used by AnalysisSettings to recolor
    async checkForOldSavedNodes() {
      if (
        this.outliersAnalysisObject.saved_node_ids &&
        this.outliersAnalysisObject.saved_node_ids.length
      ) {
        let new_ids = [];
        for (let i = 0; i < this.outliersAnalysisObject.saved_node_ids.length; i++) {
          if (
            Object.keys(this.outliersAnalysisObject.saved_node_ids[i]).length &&
            this.outliersAnalysisObject.saved_node_ids[i].uuid
          ) {
            new_ids.push(this.outliersAnalysisObject.saved_node_ids[i].uuid);
          }
        }
        if (new_ids.length) {
          this.outliersAnalysisObject.saved_node_ids = new_ids;
          this.saveTextToProject();
        }
      }
    },
    async refreshOutliers() {
      let filter_params = {};
      if (this.outliersAnalysisObject.filter_params) {
        filter_params = this.outliersAnalysisObject.filter_params;
      }
      const response = await this.OUTLIERS_SERVICE.outliersAnalysis({
        data_set_id: this.project.datasets[0],
        client_questions_ids: this.outliersAnalysisObject.selectedQuestions,
        filter_params: filter_params,
      });
      if (response) {
        if (response.data.summary_table) {
          if (!(response.data.summary_table instanceof Array)) {
            if (typeof response.data.summary_table !== 'object') {
              response.data.summary_table = [];
            } else {
              response.data.summary_table = Object.entries(response.data.summary_table);
            }
          }
          this.outliersAnalysisObject.summary_table = response.data.summary_table;
          for (let i = 0; i < this.project.outliersAnalysis.length; i++) {
            if (this.project.outliersAnalysis[i].uuid === this.outliersAnalysisObject.uuid) {
              this.project.outliersAnalysis[i].summary_table = response.data.summary_table;
              this.project.outliersAnalysis[i].last_modified_on = moment().valueOf();
            }
          }
          await this.cleanAndSaveProject();
        }
      }
    },
    async updateFilterTop(number) {
      if (!number) return;
      this.filterTop = parseInt(number);
      await this.saveOutliersToProject(this.outliersAnalysisObject.uuid);
    },

    async changeTimeFilter(timeFilter) {
      if (!timeFilter) return;

      if (this.dataset.created_on_column_id) {
        let question_id = this.dataset.created_on_column_id;
        let filter_params = {};
        if (
          this.outliersAnalysisObject.filter_params &&
          Object.keys(this.outliersAnalysisObject.filter_params).length &&
          this.outliersAnalysisObject.filter_params['operands']
        ) {
          filter_params = this.deepCloneObj(this.outliersAnalysisObject.filter_params);
        } else {
          filter_params = {
            client_question_id: question_id,
            operands: [],
            global_combine_action: 'and',
          };
        }
        const new_filter_params = await this.$_analysisMixin_calculateNewTimeFilters(
          filter_params,
          timeFilter,
        );

        this.outliersAnalysisObject.filter_params = new_filter_params;
        this.outliersAnalysisObject.time_filter_name = timeFilter;
        await this.refreshOutliers();
        //await this.saveOutliersToProject(this.outliersAnalysisObject.uuid);
      }
    },

    updateSavedSlide(savedSlide) {
      this.savedSlide = savedSlide;
    },
    // Saves an outlier which can be added to a report
    async saveThisOutlierToProject(outlier, viewMode) {
      if (outlier && viewMode) {
        let storedOutlier = {
          responses: outlier,
          viewType: viewMode,
        };
        this.outliersAnalysisObject.outliers.push(storedOutlier);
        await this.saveOutliersToProject();
      }
    },
    // When the parent prop SaveThisAnalysis fires (e.g. when using ProjectAnalysis to save the outliers)
    async saveToReport(selectedSlide, newSlideName) {
      const uuid = await this.saveOutliersToReport(selectedSlide, newSlideName);
      // this is used to get all AnalysisSetting->SaveAnalysis to rerun collections
      if (!this.project.reportSaves) {
        this.project.reportSaves = 1;
      } else {
        this.project.reportSaves = this.project.reportSaves + 1;
      }
      await this.saveOutliersToProject(uuid);
      this.$emit('refresh');
    },

    // Save to the report itself
    async saveOutliersToReport(selectedSlide, newSlideName) {
      try {
        this.isLoadingData = true;
        let createdReportID = await this.createReportIfEmpty(selectedSlide, newSlideName); // the above function will do nothing under conditions of emptiness
        if (selectedSlide) {
          createdReportID = selectedSlide.id;
        }
        const returnObject = await this.updateReportWithOutliers(
          this.outliersAnalysisObject.uuid,
          createdReportID,
          this.outliersAnalysisObject,
        );
        this.isLoadingData = false;
        await this.$emit('updateSlides');
        this.onSuccessfulSave();
        let node_ids = returnObject.newNodes.map((node) => node.uuid);
        this.outliersAnalysisObject.saved_node_ids = node_ids;
        return returnObject.uuid;
      } catch (e) {
        throw new Error('ProjectAnalysisOutliers:saveOutliersToReport ');
      }
    },

    // Save the new project nodes to this project
    async saveOutliersToProject() {
      if (this.propReadOnly) return;
      const indx = this.project.outliersAnalysis.findIndex(
        (d) => d.uuid == this.outliersAnalysisObject.uuid,
      );
      if (indx > -1) {
        let filter_params = {};
        if (this.outliersAnalysisObject.filter_params) {
          filter_params = this.outliersAnalysisObject.filter_params;
        }
        let time_filter_name = 'All data';
        if (this.outliersAnalysisObject.time_filter_name) {
          time_filter_name = this.outliersAnalysisObject.time_filter_name;
        }
        this.project.outliersAnalysis[indx].saved_node_ids =
          this.outliersAnalysisObject.saved_node_ids;
        this.project.outliersAnalysis[indx].outliers = this.outliersAnalysisObject.outliers;
        this.project.outliersAnalysis[indx].last_modified_on = moment().valueOf();
        this.project.outliersAnalysis[indx].filter_params = filter_params;
        this.project.outliersAnalysis[indx].time_filter_name = time_filter_name;
        await this.cleanAndSaveProject();
      }
    },

    async renameAnalysis(title) {
      if (this.propReadOnly) return;
      const indx = this.project.outliersAnalysis.findIndex(
        (d) => d.uuid == this.outliersAnalysisObject.uuid,
      );
      if (indx > -1) {
        this.project.outliersAnalysis[indx].analysis_title = title;
        this.project.outliersAnalysis[indx].last_modified_on = moment().valueOf();
        await this.cleanAndSaveProject();
      }
    },
    async deleteOutlier(outlierIndex) {
      this.outliersAnalysisObject.outliers.splice(outlierIndex, 1);
      this.saveOutliersToProject();
    },
    onSuccessfulSave() {
      this.setConfirmText({
        btn: 'okay',
        title: 'Outliers saved',
        message: 'Outliers successfully saved to report.',
      });
      this.setConfirmType('success');
      this.setConfirmSourceComponent('outliers');
      this.setConfirmIsVisible(true);
    },
    formatDateTime: (value, language) => {
      let currentLang = '';
      if (language) {
        currentLang = language;
      }
      if (value) {
        if (currentLang === 'fr') {
          return moment(value).locale('fr').format('MMMM D, YYYY hh:mm A').toString();
        } else {
          return moment(value).format('MMMM D, YYYY hh:mm A').toString();
        }
      }
    },
  },
  watch: {
    expandThisAnalysis: async function (val) {
      if (val) {
        this.isExpanded = true;
      } else {
        this.isExpanded = false;
      }
    },
    renameThisAnalysis: async function (val) {
      if (val) {
        if (this.$refs.AnalysisSettings) {
          this.$refs.AnalysisSettings.toggleRename();
        }
      }
    },
    saveThisAnalysis: async function (val) {
      if (val) {
        this.saveToReport(this.slideForSaving, this.slideName);
      }
    },
    setToUpdateTimeFilter: async function (val) {
      if (val) {
        await this.changeTimeFilter(this.selectedTimeFilter);
        this.$emit('updateTimeFilter', this.selectedTimeFilter);
      }
    },
    setToUpdateTimeSeries: async function (val) {
      if (val) {
        await this.changeTimeSeries(this.selectedTimeSeries);
        this.$emit('updateTimeSeries', this.selectedTimeSeries);
      }
    },
  },
};
</script>
